import type { ReactNode } from "react";
import { cn } from "@/ui/foundation/lib/utils";

export type DataListKey = string | number;
export type DataListDensity = "comfortable" | "compact";

interface DataListRenderContext {
    selected: boolean;
}

interface DataListProps<TItem> {
    items: readonly TItem[];
    getKey(item: TItem): DataListKey;
    selectedKey?: DataListKey | null;
    onSelect?: (item: TItem) => void;
    renderItem(item: TItem, context: DataListRenderContext): ReactNode;
    emptyState?: ReactNode;
    className?: string;
    itemClassName?: string;
    density?: DataListDensity;
}

const itemPaddingClassByDensity: Record<DataListDensity, string> = {
    comfortable: "p-4",
    compact: "px-3 py-2.5",
};

export function DataList<TItem>({
    items,
    getKey,
    selectedKey = null,
    onSelect,
    renderItem,
    emptyState,
    className,
    itemClassName,
    density = "comfortable",
}: DataListProps<TItem>) {
    if (items.length === 0) {
        return (
            <div className={cn("px-4 py-5 text-sm text-muted-foreground", className)}>
                {emptyState ?? "No items available."}
            </div>
        );
    }

    return (
        <div className={cn("flex flex-col", className)}>
            {items.map((item) => {
                const key = getKey(item);
                const isSelected = selectedKey !== null && key === selectedKey;
                const clickable = typeof onSelect === "function";
                const rowClassName = cn(
                    "manager-list-item w-full border-b text-left transition-colors",
                    itemPaddingClassByDensity[density],
                    isSelected ? "manager-list-item--selected" : "",
                    clickable ? "cursor-pointer" : "",
                    itemClassName
                );
                const content = renderItem(item, { selected: isSelected });

                if (!clickable) {
                    return (
                        <div key={key} className={rowClassName}>
                            {content}
                        </div>
                    );
                }

                return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onSelect(item)}
                        className={rowClassName}
                    >
                        {content}
                    </button>
                );
            })}
        </div>
    );
}
